/**
 * Square composite tile: donut (inner plan, outer actual), stacked, or clustered.
 * Click a member to open history. × hides the widget; it is not deleted.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { KpiTileShell } from './KpiTileShell';
import {
  COMPOSITE_ACTUAL_BAR,
  COMPOSITE_PLAN_BAR,
  COMPOSITE_REMAINDER_COLOR,
  COMPOSITE_SLICE_COLORS,
  fmtKpiValue,
  fmtPercent,
  type CompositeCard as CompositeCardModel,
  type CompositeSlice
} from './types';

interface CompositeCardProps {
  card: CompositeCardModel;
  t: (key: string) => string;
  onHide?: () => void;
}

function sliceColor(slice: CompositeSlice, index: number): string {
  if (!slice.kpiId) return COMPOSITE_REMAINDER_COLOR;
  return COMPOSITE_SLICE_COLORS[index % COMPOSITE_SLICE_COLORS.length];
}

function sliceLabel(slice: CompositeSlice, t: (key: string) => string): string {
  return slice.kpiId ? slice.kpiName : t('kpi.compositeRemainder') || 'Remainder';
}

export const CompositeCard: React.FC<CompositeCardProps> = ({ card, t, onHide }) => {
  const navigate = useNavigate();
  const openHistory = (kpiId: string) => {
    if (!kpiId) return;
    navigate(`/performance/kpis/${kpiId}`);
  };
  const planTotal = card.slices.reduce((sum, slice) => sum + (slice.plan || 0), 0);
  const actualTotal = card.slices.reduce((sum, slice) => sum + (slice.actual || 0), 0);
  const hasPlan = card.slices.some((slice) => slice.planPct !== null || slice.plan !== null);
  const hasActual = card.slices.some((slice) => slice.actualPct !== null || slice.actual !== null);

  return (
    <KpiTileShell
      name={card.name}
      hideLabel={t('kpi.tileHide') || 'Hide from this tab'}
      onHide={onHide}
      color={card.color}
    >
      <p className="text-[10px] text-[var(--color-secondary-text)] text-center leading-tight mb-0.5 shrink-0">
        {t('kpi.target') || 'Plan'} {fmtKpiValue(card, hasPlan ? planTotal : null)}
        {' · '}
        {t('kpi.actual') || 'Actual'} {fmtKpiValue(card, hasActual ? actualTotal : null)}
      </p>
      {card.mixError && (
        <p className="text-[10px] text-red-700 text-center">{t('kpi.compositeMix') || card.mixError}</p>
      )}
      {!hasPlan && !card.mixError && (
        <p className="text-[10px] text-[var(--color-secondary-text)] text-center">
          {t('kpi.compositeNoPlan') || 'No plan for this period.'}
        </p>
      )}
      {!hasActual && !card.mixError && (
        <p className="text-[10px] text-[var(--color-secondary-text)] text-center">
          {t('kpi.compositeNoActual') || 'No actual for this period.'}
        </p>
      )}
      {!card.mixError && card.kind === 'donut' && <Donut card={card} t={t} onOpen={openHistory} />}
      {!card.mixError && card.kind.startsWith('stack') && (
        <Stacked card={card} t={t} onOpen={openHistory} />
      )}
      {!card.mixError && card.kind.startsWith('cluster') && (
        <Clustered card={card} t={t} onOpen={openHistory} />
      )}
    </KpiTileShell>
  );
};

function Donut({
  card,
  t,
  onOpen
}: {
  card: CompositeCardModel;
  t: (key: string) => string;
  onOpen: (kpiId: string) => void;
}) {
  const planData = card.slices
    .map((slice, index) => ({
      ...slice,
      label: sliceLabel(slice, t),
      value: slice.planPct !== null && slice.planPct > 0 ? slice.planPct : 0,
      fill: sliceColor(slice, index)
    }))
    .filter((row) => row.value > 0);
  const actualData = card.slices
    .map((slice, index) => ({
      ...slice,
      label: sliceLabel(slice, t),
      value: slice.actualPct !== null && slice.actualPct > 0 ? slice.actualPct : 0,
      fill: sliceColor(slice, index)
    }))
    .filter((row) => row.value > 0);

  return (
    <div className="min-h-0 flex-1">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          {planData.length > 0 && (
            <Pie
              data={planData}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="46%"
              innerRadius="28%"
              outerRadius="48%"
              isAnimationActive={false}
              onClick={(entry) => {
                const row = entry as { kpiId?: string; payload?: { kpiId?: string } };
                onOpen(String(row.kpiId || row.payload?.kpiId || ''));
              }}
            >
              {planData.map((row) => (
                <Cell key={`plan-${row.kpiId || 'rem'}`} fill={row.fill} fillOpacity={0.45} />
              ))}
            </Pie>
          )}
          {actualData.length > 0 && (
            <Pie
              data={actualData}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="46%"
              innerRadius="55%"
              outerRadius="80%"
              isAnimationActive={false}
              onClick={(entry) => {
                const row = entry as { kpiId?: string; payload?: { kpiId?: string } };
                onOpen(String(row.kpiId || row.payload?.kpiId || ''));
              }}
            >
              {actualData.map((row) => (
                <Cell key={`act-${row.kpiId || 'rem'}`} fill={row.fill} />
              ))}
            </Pie>
          )}
          <Tooltip
            contentStyle={{ fontSize: 11 }}
            formatter={(_value: number, _name, item) => {
              const slice = item?.payload as CompositeSlice & { label?: string };
              return [
                `${t('kpi.compositeActualRing') || 'Actual'} ${fmtPercent(slice?.actualPct)} · ${
                  t('kpi.compositePlanRing') || 'Plan'
                } ${fmtPercent(slice?.planPct)}`,
                slice?.label || ''
              ];
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function Stacked({
  card,
  t,
  onOpen
}: {
  card: CompositeCardModel;
  t: (key: string) => string;
  onOpen: (kpiId: string) => void;
}) {
  const vertical = card.kind === 'stack-row';
  const keys = card.slices.map((slice, index) => ({
    key: slice.kpiId || `rem-${index}`,
    kpiId: slice.kpiId,
    name: sliceLabel(slice, t),
    fill: sliceColor(slice, index)
  }));
  const data = [
    {
      name: t('kpi.target') || 'Plan',
      ...Object.fromEntries(card.slices.map((slice, index) => [keys[index].key, slice.planPct || 0]))
    },
    {
      name: t('kpi.actual') || 'Actual',
      ...Object.fromEntries(card.slices.map((slice, index) => [keys[index].key, slice.actualPct || 0]))
    }
  ];

  return (
    <div className="min-h-0 flex-1">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout={vertical ? 'vertical' : 'horizontal'}
          margin={{ top: 2, right: 4, left: 4, bottom: 2 }}
        >
          {vertical ? (
            <>
              <XAxis type="number" hide domain={[0, 100]} />
              <YAxis type="category" dataKey="name" width={40} tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
            </>
          ) : (
            <>
              <XAxis dataKey="name" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
              <YAxis hide domain={[0, 100]} />
            </>
          )}
          <Tooltip
            contentStyle={{ fontSize: 11 }}
            formatter={(value: number, name: string) => [fmtPercent(value), name]}
          />
          {keys.map((item) => (
            <Bar
              key={item.key}
              dataKey={item.key}
              name={item.name}
              stackId="mix"
              fill={item.fill}
              isAnimationActive={false}
              onClick={() => onOpen(item.kpiId)}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Clustered({
  card,
  t,
  onOpen
}: {
  card: CompositeCardModel;
  t: (key: string) => string;
  onOpen: (kpiId: string) => void;
}) {
  const vertical = card.kind === 'cluster-row';
  const data = card.slices
    .filter((slice) => slice.kpiId)
    .map((slice) => ({
      name: slice.kpiName,
      kpiId: slice.kpiId,
      plan: slice.plan,
      actual: slice.actual
    }));
  const format = (value: number) => fmtKpiValue(card, value);

  return (
    <div className="min-h-0 flex-1">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout={vertical ? 'vertical' : 'horizontal'}
          margin={{ top: 2, right: 4, left: 4, bottom: 2 }}
        >
          {vertical ? (
            <>
              <XAxis type="number" hide domain={[0, 'auto']} />
              <YAxis type="category" dataKey="name" width={56} tick={{ fontSize: 8 }} axisLine={false} tickLine={false} />
            </>
          ) : (
            <>
              <XAxis dataKey="name" tick={{ fontSize: 8 }} interval={0} axisLine={false} tickLine={false} />
              <YAxis hide domain={[0, 'auto']} />
            </>
          )}
          <Tooltip
            contentStyle={{ fontSize: 11 }}
            formatter={(value: number, name: string) => [format(value), name]}
          />
          <Bar
            dataKey="plan"
            name={t('kpi.target') || 'Plan'}
            fill={COMPOSITE_PLAN_BAR}
            barSize={8}
            isAnimationActive={false}
            onClick={(entry) => onOpen(String(entry?.kpiId || ''))}
          />
          <Bar
            dataKey="actual"
            name={t('kpi.actual') || 'Actual'}
            fill={COMPOSITE_ACTUAL_BAR}
            barSize={8}
            isAnimationActive={false}
            onClick={(entry) => onOpen(String(entry?.kpiId || ''))}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
